'use strict';

const CURRENT_ORG_LOCAL_STORAGE_KEY = 'Organizations-Current';
const SELECTED_ORG_LOCAL_STORAGE_KEY = 'Selected-Org';
const ALL_ORGS_LOCAL_STORAGE_KEY = 'Organizations-All';

// Report generation long-polling
const REPORT_RESPONSE_FORMAT_JSON = 1;
const REPORT_POLL_INTERVAL_MS = 5000;
// Safety cap: stop polling (and surface an error) after this long if the report never
// becomes ready. Each poll rejects while the report is still generating, so without a cap
// a genuine failure would poll forever.
const REPORT_POLL_MAX_ATTEMPTS = 120; // 120 * 5s = 10 min

export default angular
  .module('main.reports.view.dataProvider', [])
  .factory('ViewReportDataProvider', function(
    $q,
    $filter,
    $state,
    $interval,
    utils,
    Resource,
    ReportsService,
    DevicesService,
    PermissionsService,
    LocationsService,
    ToastsService
  ) {

    const $t = $filter('translate');

    class ViewReportDataProvider {

      // #region Public fields

      isGlobalReport = $state.params.global === 'true';
      orgInfo = utils.getStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
      allOrgs = utils.getStorageItem(ALL_ORGS_LOCAL_STORAGE_KEY) || [];
      organizations = [];

      /**
       * Whether a report is currently being generated (async) and we're polling for it.
       * @type {boolean}
       */
      isGeneratingReport = false;

      // #region Resources

      /**
       * Available organizations
       * @type {Resource}
       */
      availableOrganizationsResource = new Resource(
        () => {
          // TODO move get all orgs stuff to the OrganizationService
          return utils.getStorageItem(ALL_ORGS_LOCAL_STORAGE_KEY) || [];
        }
      );

      /**
       * Report info resource. Loads the report and detects its schedule.
       * @type {Resource}
       */
      reportResource = new Resource(() => {
        return this.getReportInfo($state.params.reportId);
      });

      // #endregion

      /**
       * Current (active) tab.
       * @return {'details'|'group'|'history'|undefined}
       */
      get activeTab() {
        return this.#_activeTab;
      }

      /**
       * Previous tab.
       * @return {'details'|'group'|'history'|undefined}
       */
      get previousTab() {
        return this.#_previousTab;
      }

      set activeTab(val) {
        this.#_visitedTabs[val] = true;
        if (this.#_activeTab !== val) {
          this.#_previousTab = this.#_activeTab;
        }
        this.#_activeTab = val;
      }

      /**
       * Hash of visited tabs.
       */
      get visitedTabs() {
        return this.#_visitedTabs;
      }

      // #endregion

      // #region Private fields

      #_activeTab;

      #_previousTab;

      #_visitedTabs = {};

      /**
       * Report generation polling handle and attempt counter.
       */
      #reportPollingInterval;

      #reportPollingAttempts = 0;

      // #endregion

      constructor() {
        // Determine child organizations
        if (this.orgInfo) {
          this.organizations.push(this.orgInfo);
          this.allOrgs.forEach(org => {
            if (org.parentId === this.orgInfo.id) {
              this.organizations.push(org);
            }
          });
        }
      }

      // #region Public methods

      /**
       * Get report info by ID. Reuses the report passed via state params when it matches,
       * otherwise fetches it and detects its schedule.
       * @param {string|number} reportId
       * @return {Promise}
       */
      getReportInfo(reportId) {
        if ($state.params.report && $state.params.report.id.toString() === reportId.toString()) {
          return $q.resolve($state.params.report);
        }

        let orgId = !this.isGlobalReport ? this.orgInfo?.id : void 0;
        return ReportsService.getAvailableReports({ reportId: reportId, organizationId: orgId })
          .then((data) => {
            let reports = data?.reports;
            let result = reports ? data?.reports[0] : undefined;

            // Detect schedule
            if (result) {
              let groupSchedule = result.reportGroups?.find(group => !!group.schedule);
              if (groupSchedule) {
                result.schedule = groupSchedule.schedule;
                result.groupId = groupSchedule.id;
              }
            }

            return $q.resolve(result);
          });
      }

      /**
       * Generate (execute and save) a report.
       * @param {Object} params
       * @return {Promise}
       */
      generateReport(params) {
        return ReportsService.generateReport(params);
      }

      /**
       * Begin polling for asynchronous report completion using the token returned by
       * generateReport. Sets `isGeneratingReport` to true and resets it when the report
       * is ready or on failure. On success the executions cache is cleared so the history
       * list can be reloaded with the new execution.
       * @param {string} token JWT token returned by generateReport.
       */
      startReportPolling(token) {
        if (!token) {
          return;
        }
        this.stopReportPolling();
        this.isGeneratingReport = true;
        this.#reportPollingAttempts = 0;

        this.#reportPollingInterval = $interval(() => {
          this.#reportPollingAttempts++;

          ReportsService.getReportData(token, REPORT_RESPONSE_FORMAT_JSON)
            .then(() => {
              // Resolved => the report is ready.
              this.#finishReportPolling();
            }, () => {
              // Rejected => still generating. Give up (with an error) after the safety cap.
              if (this.#reportPollingAttempts >= REPORT_POLL_MAX_ATTEMPTS) {
                this.#failReportPolling();
              }
            });
        }, REPORT_POLL_INTERVAL_MS);
      }

      /**
       * Stop the report generation polling interval (does not change `isGeneratingReport`).
       */
      stopReportPolling() {
        if (this.#reportPollingInterval) {
          $interval.cancel(this.#reportPollingInterval);
          this.#reportPollingInterval = undefined;
        }
      }

      /**
       * Report finished generating: clear the loading state and drop the executions cache
       * so the history list can reload with the fresh execution.
       */
      #finishReportPolling() {
        this.stopReportPolling();
        this.isGeneratingReport = false;
        ReportsService.getReportExecutions.clearEntireCache();
      }

      /**
       * Report generation failed (or timed out): clear the loading state and notify the user.
       */
      #failReportPolling() {
        this.stopReportPolling();
        this.isGeneratingReport = false;
        ToastsService.error(null, $t('reports.errors.reportGenerationError'));
      }

      /**
       * Get report execution history.
       * @param {Object} params
       * @return {Promise}
       */
      getReportExecutions(params) {
        const parameters = {
          ...params,
          subOrgs: true,
        };
        return ReportsService.getReportExecutions(parameters);
      }

      /**
       * Get organization allowed device types with descriptions.
       * @return {Promise}
       */
      getDeviceTypes() {
        return DevicesService.getOrganizationDeviceTypes()
          .then(data => {
            let deviceTypes = utils.getOrgAllowedDeviceTypes(data.deviceTypes, null, this.orgInfo);
            if (!deviceTypes) {
              return;
            }
            for (let deviceType of deviceTypes) {
              utils.appendAttr2DeviceType(deviceType);
              let desc = $filter('getAttributeValue')(deviceType.attributes, 'name', 'desc', 'value', true);
              deviceType.desc = desc || deviceType.defaultDescription;
            }
            return deviceTypes;
          })
          .catch(err => {
            utils.handleApiError(err);
            return $q.reject();
          });
      }

      /**
       * Get timezones for the current organization country (or a default list).
       * @return {Promise}
       */
      getTimezones() {
        const orgCountryId = this.orgInfo?.country?.id;
        if (orgCountryId) {
          return LocationsService.getCountries()
            .then((countriesData) => {
              const country = countriesData.countries.find(c => c.id === orgCountryId);
              if (country?.timezones?.length > 0) {
                return country.timezones;
              } else {
                return $q.resolve(this.getDefaultTimezones());
              }
            });
        } else {
          return $q.resolve(this.getDefaultTimezones());
        }
      }

      /**
       * Server-alike list of all timezones.
       * @return {Array}
       */
      getDefaultTimezones() {
        return moment.tz.names().map(tz => {
          const tzInfo = moment.tz(tz);
          const offset = tzInfo.utcOffset();
          const dst = tzInfo.isDST();
          // Server-alike format
          return {
            id: tz,
            offset: offset,
            dst: dst,
            name: tz
          };
        });
      }

      /**
       * Download report execution file from AWS S3.
       * @param {Object} data
       * @return {Promise}
       */
      downloadData(data) {
        return utils.downloadFile(data.url);
      }

      /**
       * If current user is allowed to edit current organization.
       * @return {boolean}
       */
      canEditOrg() {
        return PermissionsService.canIEditOrganization(this.orgInfo);
      }

      /**
       * Navigate to the organization reports management tab.
       */
      onManageGroupsClick() {
        if (this.canEditOrg()) {
          utils.setStorageItem(SELECTED_ORG_LOCAL_STORAGE_KEY, this.orgInfo);
          $state.go('main.organizations.edit', { tab: 'reports' });
        }
      }

      /**
       * Universal handle function for Cancel or Back button.
       */
      goBack() {
        $state.go('main.reports.list');
      }

      /**
       * Destroy current Data Provider.
       * Obviously, you can not use this object after destruction.
       */
      destroy() {
        this.stopReportPolling();
        for (let [key, value] of Object.entries(this)) {
          if (value && value.isResource) {
            value.destroy();
            delete this[key];
          }
        }
      }

      // #endregion

    }

    return ViewReportDataProvider;
  })
  .name;
